import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useDeleteMarginFilter, useMarginFilter } from '@/services/api/hooks/catalog';

import { FunctionComponent, Suspense } from 'react';
import { toast } from 'sonner';
import { Criteria, FilterIcon } from './ManageFilters';

const DeleteMarginFilterModal: FunctionComponent<ModalProps<{ marginFilterId: string }>> = props => {
  const DeleteMarginFilter = () => {
    const marginFilterId = props.props?.marginFilterId;
    if (!marginFilterId) throw new Error('Missing margin filter id');

    const [filter] = useMarginFilter(marginFilterId);

    const deleteFilter = useDeleteMarginFilter();

    const onDelete = async () => {
      await deleteFilter.mutateAsync({ id: marginFilterId });
      props.close();
      toast.success(`Filter deleted`);
    };

    const errorObj = deleteFilter.error as Error | undefined;
    const error: string = errorObj ? errorObj.message : '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Delete filter</h1>
        </Modal.Controls>

        <div className="space-y-2">
          <p className="text-lg">
            Are you sure you want to delete:
            <div className="flex items-center">
              <FilterIcon />
              {filter.category && <Criteria label="Category" value={filter.category?.name} />}
              {filter.manufacturer && <Criteria label="Manufacturer" value={filter.manufacturer?.name} />}
              {filter.modifier && <p className="font-medium">{filter.modifier}%</p>}
            </div>
          </p>

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex gap-2 justify-end">
            <Button className="flex-1" onClick={props.close}>
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1"
              variant={ButtonVariant.Destructive}
              loading={deleteFilter.isLoading}
              onClick={onDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <DeleteMarginFilter />
      </Suspense>
    </Modal>
  );
};

export default DeleteMarginFilterModal;
